import type { ReactNode } from "react";
import Term from "@/components/Term";

/* Evergreen shastra constants only — period lengths are fixed Vimshottari values
   (rule 24: no person-specific dates/durations are ever rendered here). */
const MAHADASHA_MEANINGS: MahadashaMeaning[] = [
  {
    lord: "Ketu",
    years: 7,
    meaning:
      "Ketu Mahadasha turns life inward — spirituality, detachment, and old karmic patterns come to the surface. If you feel strangely disconnected from goals that once mattered, tradition says this is Ketu loosening your grip on the material so the inner path can open. Simple upay like seva and meditation are classically recommended during this period.",
  },
  {
    lord: "Venus",
    years: 20,
    meaning:
      "Shukra (Venus) Mahadasha is the longest period at 20 years and rules love, marriage, comfort, arts, and wealth. A well-placed Venus brings relationships, vehicles, and material ease; an afflicted Venus is the classic explanation for why relationships feel pleasant on the surface yet never fully settle.",
  },
  {
    lord: "Sun",
    years: 6,
    meaning:
      "Surya (Sun) Mahadasha activates authority, career, government matters, and the relationship with the father. It is short but intense — recognition and leadership can rise quickly, and so can ego clashes with seniors. Jyotish texts read a difficult Sun period as the reason respect feels hard-won despite genuine ability.",
  },
  {
    lord: "Moon",
    years: 10,
    meaning:
      "Chandra (Moon) Mahadasha rules the mind, emotions, mother, and public life. When the Moon is strong, mental peace and popularity flow; when afflicted, this is the period tradition points to when emotions swing without an obvious cause and sleep or restlessness becomes a pattern.",
  },
  {
    lord: "Mars",
    years: 7,
    meaning:
      "Mangal (Mars) Mahadasha brings raw energy — courage, property, siblings, and physical drive. Careers in engineering, defence, and sports classically flourish. The classic worry it explains: a temper that flares even when you do not want it to, and conflicts that seem to find you.",
  },
  {
    lord: "Rahu",
    years: 18,
    meaning:
      "Rahu Mahadasha is 18 years of worldly ambition — foreign lands, technology, unconventional success, and sudden rises. Its shadow side is the mind that will not switch off: obsessive loops, restlessness, and desires that grow the moment they are fulfilled. Tradition treats this period as transformative, not cursed.",
  },
  {
    lord: "Jupiter",
    years: 16,
    meaning:
      "Guru (Jupiter) Mahadasha is generally the most auspicious period — wisdom, children, education, dharma, and wealth expand through righteous means. Teachers and mentors appear. Even here, an afflicted Jupiter is the traditional explanation for good judgment arriving late, after the decision is already made.",
  },
  {
    lord: "Saturn",
    years: 19,
    meaning:
      "Shani (Saturn) Mahadasha is the great teacher — 19 years of discipline, karma, and slow, earned results. It is the classic answer to “why does my hard work never turn into results?”: Shani delays but does not deny, rewarding sustained effort with stability that no other period can give.",
  },
  {
    lord: "Mercury",
    years: 17,
    meaning:
      "Budh (Mercury) Mahadasha sharpens intellect, communication, business, and trade. Writers, analysts, and entrepreneurs classically thrive. When Budh is weak or afflicted, this is the period tradition names for scattered focus — many ideas started, few finished.",
  },
];

type MahadashaMeaning = { lord: string; years: number; meaning: string };

export function MahadashaMeanings({ locale = "en" }: { locale?: "en" | "hi" } = {}) {
  if (locale === "hi") {
    return (
      <MahadashaMeaningsLayout
        heading="हर महादशा का अर्थ"
        intro={
          <>
            हर महादशा किसी एक ग्रह के कारकत्व को जीवन का मुख्य विषय बना देती है। नीचे दी गई अवधियां
            शास्त्रों में वर्णित विंशोत्तरी की निश्चित अवधियां हैं; कोई दशा वास्तव में कैसे फल देगी,
            यह उस ग्रह के बल और कुंडली में उसकी स्थिति पर निर्भर है। कुंडली प्रवृत्तियां दिखाती है,
            निश्चित परिणाम नहीं।
          </>
        }
        items={MAHADASHA_MEANINGS_HI}
        itemHeading={(m) => `${m.lord} महादशा — ${m.years} वर्ष`}
        antarHeading="अंतर्दशा बदलने पर क्या बदलता है"
        antarLead={
          <>
            हर महादशा के भीतर अंतर्दशा (भुक्ति) का स्वामी कुछ महीनों से लेकर कुछ वर्षों में बदलता
            रहता है, और उसके साथ उस काल का स्वरूप भी बदलता है। महादशा का स्वामी मकान-मालिक है;
            अंतर्दशा का स्वामी किरायेदार, जो तय करता है कि घर रोज़-रोज़ कैसा लगेगा। इसीलिए एक ही शनि
            महादशा किसी चरण में भारी लगती है और अगले चरण में चुपचाप फलदायी।
          </>
        }
        antarDetail={
          <>
            अंतर्दशा बदलने पर ज्योतिष परंपरा तीन चीज़ों में बदलाव की अपेक्षा करती है: जीवन के कौन से
            क्षेत्र केंद्र में हैं (नए उप-स्वामी के भाव सक्रिय होते हैं), फल मिलने की गति (मित्र
            महादशा–अंतर्दशा स्वामी फल को सहज बनाते हैं, शत्रु स्वामी घर्षण पैदा करते हैं), और मन का
            भाव। अगर पिछले कुछ महीनों में बिना किसी बाहरी कारण के जीवन में साफ़ बदलाव आया है, तो
            देखें कि कहीं आपकी अंतर्दशा तो नहीं बदली — परंपरा में अक्सर वही संक्रमण, न कि कोई एक
            घटना, इसकी व्याख्या होती है।
          </>
        }
      />
    );
  }
  return (
    <MahadashaMeaningsLayout
      heading="What Each Mahadasha Period Means"
      intro={
        <>
          Every <Term k="mahadasha">Mahadasha</Term> activates one planet&apos;s significations as
          the dominant theme of life. The period lengths below are fixed Vimshottari values from the
          classical texts; how each period actually unfolds depends on that planet&apos;s strength
          and placement in your kundali. The chart shows tendencies, not fixed outcomes.
        </>
      }
      items={MAHADASHA_MEANINGS}
      itemHeading={(m) => `${m.lord} Mahadasha — ${m.years} Years`}
      antarHeading="What Changes When Your Antardasha Shifts"
      antarLead={
        <>
          Within every Mahadasha, the <Term k="antardasha">Antardasha</Term> (Bhukti) lord changes
          every few months to a few years, and with it the texture of the period changes. The
          Mahadasha lord stays the landlord; the Antardasha lord is the tenant who decides how the
          house feels day to day. This is why the same Shani Mahadasha can feel crushing in one
          phase and quietly productive in the next.
        </>
      }
      antarDetail={
        <>
          When an Antardasha shifts, Jyotish tradition expects three things to move: the life areas
          in focus (the new sub-lord&apos;s houses activate), the pace of results (friendly
          Mahadasha&ndash;Antardasha pairs ease delivery, inimical pairs create friction), and the
          emotional tone. If life changed noticeably in recent months with no outer cause, check
          whether your Antardasha changed &mdash; that transition, not a single event, is often the
          traditional explanation.
        </>
      }
    />
  );
}

/** One layout for both locales, so the page markup (and its styling) exists once. */
function MahadashaMeaningsLayout({
  heading,
  intro,
  items,
  itemHeading,
  antarHeading,
  antarLead,
  antarDetail,
}: {
  heading: string;
  intro: ReactNode;
  items: MahadashaMeaning[];
  itemHeading: (m: MahadashaMeaning) => string;
  antarHeading: string;
  antarLead: ReactNode;
  antarDetail: ReactNode;
}) {
  return (
    <div className="cv-auto [contain-intrinsic-size:auto_2400px]">
      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        {heading}
      </h2>
      <p className="text-body leading-relaxed mb-6">{intro}</p>
      <div className="space-y-5 mb-6">
        {items.map((m) => (
          <div key={m.lord}>
            <h3 className="text-body font-bold mb-1" style={{ color: "var(--ish-dark)" }}>
              {itemHeading(m)}
            </h3>
            <p className="text-body leading-relaxed">{m.meaning}</p>
          </div>
        ))}
      </div>

      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        {antarHeading}
      </h2>
      <p className="text-body leading-relaxed mb-4">{antarLead}</p>
      <p className="text-body leading-relaxed mb-6">{antarDetail}</p>
    </div>
  );
}

/* Hindi reading of the same evergreen Vimshottari constants (same lords, same
   fixed years) for /hi/dasha-calculator/. */
const MAHADASHA_MEANINGS_HI: MahadashaMeaning[] = [
  {
    lord: "केतु",
    years: 7,
    meaning:
      "केतु महादशा जीवन को भीतर की ओर मोड़ती है — आध्यात्मिकता, वैराग्य और पुराने कर्म-संस्कार सतह पर आते हैं। जो लक्ष्य कभी बहुत मायने रखते थे, उनसे अजीब-सी दूरी महसूस हो, तो परंपरा इसे केतु का भौतिक पकड़ ढीली करना मानती है, ताकि भीतर का मार्ग खुल सके। इस काल में सेवा और ध्यान जैसे सरल उपाय शास्त्रीय रूप से सुझाए जाते हैं।",
  },
  {
    lord: "शुक्र",
    years: 20,
    meaning:
      "शुक्र महादशा 20 वर्ष की सबसे लंबी दशा है और प्रेम, विवाह, सुख-सुविधा, कला तथा धन से जुड़ी है। शुभ स्थिति का शुक्र संबंध, वाहन और भौतिक आराम देता है; पीड़ित शुक्र वह पारंपरिक कारण माना जाता है जिससे रिश्ते ऊपर से मधुर दिखते हैं, पर कभी पूरी तरह स्थिर नहीं होते।",
  },
  {
    lord: "सूर्य",
    years: 6,
    meaning:
      "सूर्य महादशा अधिकार, करियर, शासन से जुड़े कार्य और पिता के साथ संबंध को सक्रिय करती है। यह छोटी पर तीव्र दशा है — पहचान और नेतृत्व तेज़ी से बढ़ सकते हैं, और वरिष्ठों से अहं का टकराव भी। ज्योतिष ग्रंथ कठिन सूर्य दशा को इस बात का कारण मानते हैं कि सच्ची योग्यता के बावजूद सम्मान बड़ी मुश्किल से मिलता है।",
  },
  {
    lord: "चंद्र",
    years: 10,
    meaning:
      "चंद्र महादशा मन, भावनाओं, माता और सार्वजनिक जीवन से जुड़ी है। बलवान चंद्रमा में मानसिक शांति और लोकप्रियता सहज मिलती है; पीड़ित होने पर परंपरा इसी काल को वह समय बताती है जब भावनाएं बिना स्पष्ट कारण के ऊपर-नीचे होती हैं और नींद की कमी या बेचैनी आदत बन जाती है।",
  },
  {
    lord: "मंगल",
    years: 7,
    meaning:
      "मंगल महादशा प्रबल ऊर्जा लाती है — साहस, भूमि-संपत्ति, भाई-बहन और शारीरिक दमखम। इंजीनियरिंग, रक्षा सेवा और खेल से जुड़े करियर पारंपरिक रूप से फलते-फूलते हैं। यह दशा उस परेशानी को भी समझाती है जिसमें न चाहते हुए भी गुस्सा भड़क उठता है और झगड़े खुद चलकर आते हैं।",
  },
  {
    lord: "राहु",
    years: 18,
    meaning:
      "राहु महादशा 18 वर्षों की सांसारिक महत्वाकांक्षा है — विदेश, तकनीक, लीक से हटकर सफलता और अचानक उन्नति। इसका छाया पक्ष वह मन है जो रुकता ही नहीं: बार-बार घूमते विचार, बेचैनी और ऐसी इच्छाएं जो पूरी होते ही और बढ़ जाती हैं। परंपरा इस काल को अभिशाप नहीं, परिवर्तन का समय मानती है।",
  },
  {
    lord: "गुरु",
    years: 16,
    meaning:
      "गुरु (बृहस्पति) महादशा सामान्यतः सबसे शुभ दशा मानी जाती है — ज्ञान, संतान, शिक्षा, धर्म और धन धर्मसम्मत मार्ग से बढ़ते हैं। शिक्षक और मार्गदर्शक जीवन में आते हैं। फिर भी, पीड़ित गुरु को पारंपरिक रूप से इस बात का कारण माना जाता है कि सही समझ निर्णय हो जाने के बाद देर से आती है।",
  },
  {
    lord: "शनि",
    years: 19,
    meaning:
      "शनि महादशा महान शिक्षक है — 19 वर्षों का अनुशासन, कर्म और धीरे-धीरे अर्जित फल। “मेरी मेहनत का फल क्यों नहीं मिलता?” — इस प्रश्न का पारंपरिक उत्तर यही दशा है: शनि देर करता है, पर इनकार नहीं करता, और लगातार परिश्रम को ऐसी स्थिरता से पुरस्कृत करता है जो कोई और दशा नहीं दे पाती।",
  },
  {
    lord: "बुध",
    years: 17,
    meaning:
      "बुध महादशा बुद्धि, संवाद, व्यापार और लेन-देन को प्रखर करती है। लेखक, विश्लेषक और उद्यमी पारंपरिक रूप से इस काल में आगे बढ़ते हैं। बुध निर्बल या पीड़ित हो, तो परंपरा इसी काल को बिखरे ध्यान का समय बताती है — कई काम शुरू होते हैं, पूरे कम होते हैं।",
  },
];
